import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, Trophy, CreditCard } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { formatPrice } from "@/utils/formatPrice";

// Matches get-public-landing edge function response for tournament
interface TournamentEntity {
  name: string;
  format: string;
  format_variant: string | null;
  description: string | null;
  date: string;
  start_time: string;
  end_time: string | null;
  max_players: number;
  price_per_player: number | null;
  status: string;
  registered_count: number;
}

interface Props {
  entity: Record<string, unknown>;
  onAction: (path: string) => void;
  brandColor?: string;
}

export default function TournamentLanding({ entity, onAction, brandColor }: Props) {
  const tournament = entity as unknown as TournamentEntity;
  const date = parseISO(tournament.date);
  const registered = tournament.registered_count;
  const maxPlayers = tournament.max_players;

  const formatLabel: Record<string, string> = {
    americano: "Americano",
    mexicano: "Mexicano",
    round_robin: "Round Robin",
    bracket: "Slutspel",
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{tournament.name}</h1>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="gap-1">
            <Trophy className="h-3 w-3" />
            {formatLabel[tournament.format] ?? tournament.format}
          </Badge>
          {tournament.format_variant && (
            <Badge variant="outline">{tournament.format_variant}</Badge>
          )}
        </div>
      </div>

      {tournament.description && (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
          {tournament.description}
        </p>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{format(date, "EEEE d MMMM yyyy", { locale: sv })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {tournament.start_time.slice(0, 5)}
              {tournament.end_time ? `–${tournament.end_time.slice(0, 5)}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span>
              {registered}/{maxPlayers} anmälda
            </span>
          </div>
          {tournament.price_per_player != null && tournament.price_per_player > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span>{formatPrice(tournament.price_per_player)} / spelare</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action */}
      <Button
        className="w-full text-base py-6"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        disabled={tournament.status !== "upcoming" && tournament.status !== "open"}
        onClick={() => onAction("/tournaments")}
      >
        {registered >= maxPlayers
          ? "Fullbokat"
          : "Anmäl lag →"}
      </Button>
    </div>
  );
}
